import { useEffect, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { MoreHorizontal } from 'lucide-react';
import { Spinner } from '../../../components/ui/Spinner';
import { ErrorState } from '../../../components/ui/States';
import {
  downloadMarketingExportQuick,
  startMarketingExportRun,
  getMarketingExportRun,
} from '../../../api/dashboard';

/** Trigger a browser download of a text payload. */
function downloadText(content: string, fileName: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function downloadBlobFile(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

const POLL_INTERVAL_MS = 4000;
/** Safety cap: the full crawl is ~3–5 min; give up well after that. */
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Downloads the all-hands marketing export (Markdown brief + CSV) for Lea,
 * from a ⋯ menu so the two rarely-used actions don't crowd the period control.
 *
 * Two paths, because the accurate "pages not indexed" count needs a full
 * sitemap crawl that takes a few minutes — longer than request timeouts allow:
 *  - **Full report**: starts a background job and polls until ready, then
 *    downloads the brief + CSV. This is the accurate one.
 *  - **Quick draft**: synchronous download using the fast daily index sample.
 */
export function MarketingExportMenu() {
  const [isFull, setIsFull] = useState(false);
  const [isQuick, setIsQuick] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleQuick = async () => {
    setError(null);
    setIsQuick(true);
    try {
      const [md, csv] = await Promise.all([
        downloadMarketingExportQuick('md'),
        downloadMarketingExportQuick('csv'),
      ]);
      downloadBlobFile(md.blob, md.fileName ?? 'marketing-allhands-export.md');
      downloadBlobFile(csv.blob, csv.fileName ?? 'marketing-allhands-export.csv');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to download the quick marketing export.');
    } finally {
      if (mounted.current) setIsQuick(false);
    }
  };

  const handleFull = async () => {
    setError(null);
    setIsFull(true);
    setStatusMsg('Starting… crawling the sitemap and checking index status (a few minutes).');
    try {
      const { runId } = await startMarketingExportRun();
      const deadline = Date.now() + POLL_TIMEOUT_MS;

      // Poll until the background job finishes.
      while (true) {
        if (Date.now() > deadline) throw new Error('Export timed out. Try again, or use the quick draft.');
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (!mounted.current) return;

        const run = await getMarketingExportRun(runId);
        if (run.status === 'running') {
          setStatusMsg('Still running… inspecting sitemap pages for index status.');
          continue;
        }
        if (run.status === 'error') throw new Error(run.error || 'Export failed.');

        // ready
        const base = run.fileName.replace(/\.md$/, '');
        downloadText(run.markdown, run.fileName, 'text/markdown;charset=utf-8');
        downloadText(run.csv, `${base}.csv`, 'text/csv;charset=utf-8');
        setStatusMsg(`Done: downloaded ${run.fileName} + CSV.`);
        return;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate the marketing export.');
      setStatusMsg(null);
    } finally {
      if (mounted.current) setIsFull(false);
    }
  };

  const busy = isFull || isQuick;

  // The "Done" line is a confirmation, not state worth keeping on screen.
  useEffect(() => {
    if (!statusMsg?.startsWith('Done')) return;
    const t = setTimeout(() => setStatusMsg(null), 6000);
    return () => clearTimeout(t);
  }, [statusMsg]);

  const item =
    'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-body-sm text-text-primary hover:bg-surface-secondary disabled:opacity-50';

  return (
    <div className="relative flex flex-col items-end gap-1">
      <Popover className="relative">
        <PopoverButton
          aria-label="Exports"
          className="flex h-7 w-7 items-center justify-center rounded-full border border-border-light text-text-secondary hover:bg-surface-secondary focus:outline-none"
        >
          {busy ? <Spinner size="sm" /> : <MoreHorizontal className="h-4 w-4" aria-hidden />}
        </PopoverButton>
        <PopoverPanel
          anchor="bottom end"
          className="z-50 mt-1 w-64 rounded-lg border border-border-light bg-surface-primary p-1 shadow-medium [--anchor-gap:4px]"
        >
          {({ close }) => (
            <>
              <button type="button" className={item} disabled={busy} onClick={() => { close(); void handleFull(); }}>
                <span>
                  Marketing export
                  <span className="block text-body-xs text-text-tertiary">Full crawl, takes a few minutes</span>
                </span>
              </button>
              <button type="button" className={item} disabled={busy} onClick={() => { close(); void handleQuick(); }}>
                <span>
                  Quick draft
                  <span className="block text-body-xs text-text-tertiary">Fast, uses the daily index sample</span>
                </span>
              </button>
            </>
          )}
        </PopoverPanel>
      </Popover>
      {statusMsg && !error && (
        <p className="absolute right-0 top-full z-10 mt-1 w-72 rounded-md bg-surface-primary p-2 text-right text-body-xs text-text-secondary shadow-subtle">
          {statusMsg}
        </p>
      )}
      {error && (
        <div className="absolute right-0 top-full z-10 mt-1 w-80">
          <ErrorState compact title="Marketing export failed" error={error} onRetry={() => void handleFull()} />
        </div>
      )}
    </div>
  );
}
